import * as React from 'react';
import Styled, { css } from 'styled-components';

import SVGIcon, { ISVGIconProps } from '../Icon/SVGIcon';

export interface ITooltipState {
  open: boolean;
}

const TooltipButton = Styled.button`
  position: relative;
  z-index: 110;
  color: ${({ theme: { link } }) => link.colorLink};

  &:hover {
    color: ${({ theme: { link } }) => link.colorLinkHover};
  }
`;

export const TooltipIcon = Styled(SVGIcon)<ISVGIconProps>`
  width: 18px;
  position: relative;
  top: 0.2em;
  fill: CurrentColor;
`;

const TooltipPane = Styled.span`
  transition: opacity 100ms ease-out;
  margin-top: 0.75em;
  padding: 1em 1.25em;
  position: absolute;
  background: ${({theme}) => theme.toolTip.paneBackgroundColor};
  line-height: 1.4;
  border-radius: ${({theme}) => theme.toolTip.paneBorderRadius};
  box-shadow: ${({theme}) => theme.toolTip.paneBoxShadow};
  z-index: -111;
  opacity: 0;
  max-width: 25em;
  display: none;
  font-size: ${({theme}) => theme.toolTip.paneFontSize};

  ${({ theme: { toolTip }}) => css`
    color: ${toolTip.fontColor && toolTip.fontColor};
    border: ${toolTip.border && toolTip.border};
    font-family: ${toolTip.paneFontFamily && toolTip.paneFontFamily};
  `}

  &[aria-hidden='false'] {
    display: block;
    opacity: 1;
    z-index: 222;
  }
`;

export interface ITooltipProps {
  tooltipId: string;
}

export default class Tooltip extends React.PureComponent<
  ITooltipProps,
  ITooltipState
> {
  public state: ITooltipState = {
    open: false
  };

  private readonly tooltipLink = React.createRef<HTMLButtonElement>();
  private readonly tooltipPane = React.createRef<HTMLSpanElement>();

  public componentDidMount () {
    document.addEventListener('click', this.handleClick, true);
    document.addEventListener('keydown', this.handleEscapePress);
  }

  public componentWillUnmount () {
    document.removeEventListener('click', this.handleClick, true);
    document.removeEventListener('keydown', this.handleEscapePress, true);
  }

  public render () {
    const { children, tooltipId, ...rest } = this.props;

    return (
      <div {...rest}>
        <TooltipButton
          aria-label='Tooltip Icon'
          aria-describedby={tooltipId}
          ref={this.tooltipLink}
          onClick={this.toggleTooltip}
          onBlur={this.handleOnBlur}
        >
          <TooltipIcon icon='tooltip' />
        </TooltipButton>
        <TooltipPane
          role='tooltip'
          aria-hidden={!this.state.open}
          id={tooltipId}
          ref={this.tooltipPane}
        >
          {children}
        </TooltipPane>
      </div>
    );
  }

  private readonly handleOnBlur = () => this.setState({ open: false });

  private readonly toggleTooltip: React.MouseEventHandler<HTMLElement> = e => {
    this.setState(({ open }) => ({ open: !open }));
    e.preventDefault();
    e.stopPropagation();
  }

  private readonly handleEscapePress: (e: KeyboardEvent) => void = e => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      this.setState({ open: false });
    }
  }

  private readonly handleClick: (e: MouseEvent) => void = e => {
    if (!this.state.open) {
      return;
    }

    const tooltipLinkContent = this.tooltipLink.current;
    const tooltipPaneContent = this.tooltipPane.current;
    const targetElement: Element = e.target as Element;

    if (
      !tooltipLinkContent ||
      !tooltipPaneContent ||
      targetElement === tooltipPaneContent ||
      targetElement === tooltipLinkContent ||
      tooltipPaneContent.contains(targetElement) ||
      tooltipLinkContent.contains(targetElement)
    ) {
      return;
    }

    this.setState({ open: false });
  }
}
